import { isNodeInPreservedMove } from "@/src/shared/utils/connectedNodeMove";

type FrameControllerLike = {
  element: Element;
  disconnect: (this: FrameControllerLike) => void;
};

type FrameElementConstructor = CustomElementConstructor & {
  delegateConstructor?: { prototype: FrameControllerLike };
};

const TURBO_FRAME_TAG = "turbo-frame";
/** Global symbol, so a re-evaluated module (HMR, duplicated chunk) recognises an already patched prototype. */
const PATCHED_KEY = Symbol.for("easy8.turboFrameMovePreservation");

const frameControllerPrototype = (): FrameControllerLike | undefined => {
  const frameElement = customElements.get(TURBO_FRAME_TAG) as FrameElementConstructor | undefined;
  const prototype = frameElement?.delegateConstructor?.prototype;

  return typeof prototype?.disconnect === "function" ? prototype : undefined;
};

/**
 * Keeps a Turbo frame controller connected while its frame is inside a synchronous slot move made through
 * `moveNodesBefore` (custom element slot projection). Without this, Turbo 8 treats the transient
 * `disconnectedCallback` as a real removal and cancels the in-flight frame request. Real removals and unmarked
 * DOM moves still disconnect and cancel as before.
 *
 * Element callbacks are captured by `customElements.define`, so the patch targets the frame's delegate
 * (`FrameController.prototype.disconnect`) rather than the element class. `connect` stays untouched so a frame
 * that was really detached reloads when it is inserted again.
 *
 * @returns `true` when `turbo-frame` is defined (patched now or earlier), `false` when Turbo is not loaded.
 */
export const installTurboFrameMovePreservation = (): boolean => {
  const prototype = frameControllerPrototype();
  if (!prototype) return false;
  if (PATCHED_KEY in prototype) return true;

  const originalDisconnect = prototype.disconnect;
  prototype.disconnect = function disconnect(this: FrameControllerLike) {
    if (this.element.isConnected && isNodeInPreservedMove(this.element)) return;
    originalDisconnect.call(this);
  };
  Object.defineProperty(prototype, PATCHED_KEY, { value: true });

  return true;
};
